/**
 * <ResilientImg> — drop-in replacement for <img> that automatically
 * retries sibling file extensions before falling back to a placeholder.
 *
 * Fixes two recurring bug classes:
 *
 *   1) "DB and disk drift" — DB points at /products/foo.png, but the
 *      file on disk is /products/foo.jpg (e.g., after a migration).
 *      → On error, swap the extension through jpg → jpeg → png → webp
 *      → gif → svg until one renders.
 *
 *   2) "File never written" — DB points at /products/foo.jpg, but no
 *      file with stem `foo` exists in any format on disk (e.g., the
 *      seeder created the DB row but the image generator failed, or
 *      the product was added manually via the admin form with a path
 *      that doesn't exist).
 *      → After exhausting all extensions, render a branded inline SVG
 *      placeholder with the product name on a dark gradient. Looks
 *      intentional, on-brand for theailedger, never the generic
 *      Unsplash photo.
 *
 * Why a component (not just an onError handler): React 18+
 * reconciliation will clobber direct DOM-attribute mutations in
 * onError when the parent re-renders. Using component state means
 * React owns the src and the retry sticks.
 *
 * Usage:
 *   <ResilientImg src={url} alt="Product name" className="..." />
 *
 * The `alt` text is reused as the placeholder label, so passing a
 * descriptive alt is doubly important for accessibility AND fallback
 * quality.
 */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { PLACEHOLDER_IMAGE } from '@/utils/imageUtils';

const _EXT_FALLBACKS = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'svg'];

function _stripQuery(u) {
  const i = u.indexOf('?');
  return i >= 0 ? [u.slice(0, i), u.slice(i)] : [u, ''];
}

/**
 * Build a branded SVG data URI placeholder bearing the product name.
 *
 * Style mirrors the marketplace's dark/cyan aesthetic so an
 * image-less product card still feels native instead of broken. We
 * compute it lazily (only when we actually need it) and memoize on
 * the label so re-renders don't rebuild the URI on every paint.
 */
function _brandedPlaceholder(label) {
  const text = (label || 'theailedger').trim().slice(0, 28);
  // Escape minimum SVG characters for safety in attribute context.
  const safe = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

  // Single-glyph monogram: first non-whitespace letter of the label.
  const glyph = (text.match(/[A-Za-z0-9]/) || [''])[0].toUpperCase();

  // Hash the label to pick a deterministic accent hue so visually
  // distinct products don't collide on the same placeholder.
  let hash = 0;
  for (let i = 0; i < text.length; i++) {
    hash = (hash * 31 + text.charCodeAt(i)) | 0;
  }
  const hue = Math.abs(hash) % 360;
  const accent = `hsl(${hue}, 80%, 60%)`;

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 400" preserveAspectRatio="xMidYMid slice">
      <defs>
        <linearGradient id="bg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stop-color="#0a0a0a"/>
          <stop offset="100%" stop-color="#171717"/>
        </linearGradient>
        <radialGradient id="glow" cx="50%" cy="40%" r="60%">
          <stop offset="0%" stop-color="${accent}" stop-opacity="0.35"/>
          <stop offset="100%" stop-color="${accent}" stop-opacity="0"/>
        </radialGradient>
      </defs>
      <rect width="400" height="400" fill="url(#bg)"/>
      <rect width="400" height="400" fill="url(#glow)"/>
      <circle cx="200" cy="170" r="64" fill="none" stroke="${accent}" stroke-width="2" stroke-opacity="0.7"/>
      <text x="200" y="192"
            text-anchor="middle"
            font-family="'Outfit', 'Inter', system-ui, sans-serif"
            font-size="68"
            font-weight="800"
            fill="${accent}">${glyph}</text>
      <text x="200" y="290"
            text-anchor="middle"
            font-family="'Inter', system-ui, sans-serif"
            font-size="22"
            font-weight="600"
            fill="#fafafa">${safe}</text>
    </svg>
  `.trim();

  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

export default function ResilientImg({
  src,
  fallback,
  alt = '',
  onError,
  ...rest
}) {
  // Anchored to the *original* src that the parent passed in. If the
  // parent re-renders with a different src, we reset.
  const [currentSrc, setCurrentSrc] = useState(src);
  const triedRef = useRef([]);

  // Memoize the branded placeholder so we don't rebuild it on every
  // render. Falls back to PLACEHOLDER_IMAGE if the caller explicitly
  // overrode the fallback prop.
  const brandedFallback = useMemo(
    () => _brandedPlaceholder(alt),
    [alt]
  );
  const effectiveFallback = fallback ?? brandedFallback ?? PLACEHOLDER_IMAGE;

  useEffect(() => {
    setCurrentSrc(src);
    triedRef.current = [];
  }, [src]);

  function handleError(e) {
    // Forward to a caller-supplied handler if any.
    if (typeof onError === 'function') {
      try { onError(e); } catch { /* swallow */ }
    }

    const [pathPart, qs] = _stripQuery(currentSrc || '');
    const match = pathPart.match(/^(.+)\.([a-z]+)$/i);
    if (!match) {
      setCurrentSrc(effectiveFallback);
      return;
    }
    const [, stem, ext] = match;
    if (!triedRef.current.includes(ext.toLowerCase())) {
      triedRef.current.push(ext.toLowerCase());
    }
    const next = _EXT_FALLBACKS.find((x) => !triedRef.current.includes(x));
    if (next) {
      setCurrentSrc(`${stem}.${next}${qs}`);
    } else {
      setCurrentSrc(effectiveFallback);
    }
  }

  // If the placeholder itself fails (shouldn't — data URI), detach
  // to avoid loops.
  function handlePlaceholderError(e) {
    e.currentTarget.onerror = null;
  }

  const isPlaceholder = currentSrc === effectiveFallback;

  return (
    <img
      {...rest}
      alt={alt}
      src={currentSrc || effectiveFallback}
      onError={isPlaceholder ? handlePlaceholderError : handleError}
    />
  );
}
